<template>
<form
	data-component="FormGroup.vue"
	class="[ form-group ] [&>*:first-child]:!mt-0"
	:autocomplete="PreventAutocomplete ? 'off' : ''"
	:class="{
		'is-readonly': Readonly,
		'text-neutral-600': Readonly,
		'is-editable': !Readonly,
		'is-multicolumn': ScreenSize > 1,
		'p-5': !noPadding,
	}"
>
	<template v-for="(Field, Index) of Form">
		<template v-if="!Field.Display || (Field.Display !== 'none' && (Field.Display === 'edit' && !Readonly) || (Field.Display === 'not-empty' && (!Readonly || (Readonly && Field.Value))))">
			<template v-if="Field.Type === 'Static'">
				<slot :name="Field.Name">
					<div :key="Index" :class="Field.Class" @click="OnStaticClick(Field, $event)" v-html="Field.Value"/>
				</slot>
			</template>
			<div v-if="Field.Type === 'Var'" :key="Index" hidden>
				{{ Field.Name + ': ' + Field.Value }}
			</div>
			<div v-if="Field.Type === 'Boolean'" :id="Field.Name + '-wrapper'" :key="Index" class="form-input" :class="Field.Class" :data-checked="Field.Value" :data-invalid="Boolean(Field.ValidationMessage)">
				<template v-if="!Field.Toggle">
					<label class="form-input-checkbox">
						<div class="input-control">
							<input :id="Field.Name" v-model="Field.Value" type="checkbox" :data-test="Field.DataTest" :disabled="Field.Disabled" @change="SetAsDirty(Field, $event); OnInput(Field, $event)"><div class="i"/>
						</div>
						<span v-html="Field.Label "/>
					</label>
				</template>
				<template v-else>
					<label class="flex items-center pb-3 border-b form-input-checkbox border-black/10">
						<slot :name="`${Field.Name}Label`">
							<span v-html="Field.Label"/>
						</slot>
						<hr class="flex-auto w-2">
						<div class="input-control input-control-toggle">
							<input :id="Field.Name" v-model="Field.Value" :disabled="Field.Disabled" :data-test="Field.DataTest" type="checkbox" @change="SetAsDirty(Field, $event); OnInput(Field, $event)"><div/>
						</div>
					</label>
				</template>
				<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
			</div>
			<div v-if="Field.Type === 'Input'" :id="Field.Name + '-wrapper'" :key="Index" class="form-input" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div v-if="Readonly" class="flex items-center">
					<i v-if="typeof Field.Emblem === 'string'" class="mr-2 form-input-emblem text-neutral-600" v-html="Field.Emblem"/>
					<OIcon v-if="Field.Emblem" size="1.75" class="mr-2" :name="Field.Emblem.Name"/>
					<span class="form-input-value" :data-test="Field.DataTest" v-html="Field.Value ? Field.Value : '(brak)'"/>
				</div>
				<div v-else class="relative" :class="{ 'is-password-field': GetFieldInputType(Field) === 'password' }">
					<div v-if="Field.Autocomplete">
						<autocomplete :ref="Field.Name" v-model="Field.Value" :Autocomplete="Field.Autocomplete" :Suggestions="Field.Suggestions" :IgnoreValues="Field.IgnoreValues" @input="OnInput(Field, $event)"/>
					</div>
					<input v-else :id="Field.Name" v-model="Field.Value" :type="GetFieldInputType(Field)" :class="{ '!pl-12': Field.HelperIcon }" :disabled="Field.Disabled" :placeholder="Field.Placeholder" :maxlength="Field.Max" :data-test="Field.DataTest" @keydown="OnKeyDown(Field, $event)" @keyup="SetAsDirty" @mousewheel="preventMousewheelOnNumericInput" @keydown.enter.prevent @change="OnInput(Field, $event)">
					<div v-if="Field.Currency" class="absolute top-0 bottom-0 right-0 flex items-center justify-center mx-4 text-neutral-600">
						zł
					</div>
					<div v-if="GetFieldInputType(Field) === 'password'" class="form-input-password-toggle | absolute right-0 top-0 p-4 text-neutral-400" @click="TogglePassword">
						<OIcon class="form-input-password-toggle-on" size="1.5" name="eye-outlined-on"/>
						<OIcon class="form-input-password-toggle-off" size="1.5" name="eye-outlined-off"/>
					</div>
					<OIcon v-if="Field.Icon" class="absolute -translate-y-1/2 right-4 top-1/2 text-accent" :class="{'cursor-pointer': Field.IconAction}" :name="Field.Icon" @click="Field.IconAction"/>
					<OIcon v-if="Field.HelperIcon" v-bind="Field.HelperIcon" class="absolute top-0 left-0 m-4 pointer-events-none text-neutral-400" :size="1.5"/>
				</div>
				<label :for="Field.Name" class="flex items-center form-input-label">
					<slot :name="`${Field.Name}Label`">
						<span v-if="Field.Required" class="mr-1 text-danger">*</span>
						<div class="pr-2" v-html="Field.Label"/>
					</slot>
				</label>
				<div v-if="Field.Hint" class="form-input-validation form-input-hint | flex items-center gap-1 text-neutral-600 text-xs mt-2">
					<OIcon v-if="Field.HintIcon" class="text-info" name="info-information-circle" size="1.25"/>
					<span v-html="Field.Hint"/>
				</div>
				<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
			</div>

			<div v-if="Field.Type === 'DateTime'" :id="Field.Name + '-wrapper'" :key="Index" class="form-input" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div class="relative flex">
					<DatePicker :id="Field.Name + 'date'" v-model="Field.Value[0]" :value="Field.Value[0]" :Disabled="Field.Disabled" :Min="Field.Min || '2020-01-01'" @update:model-value="OnInput(Field, $event)"/>
					<hr style="flex: 0 0 8px;">
					<TimePicker v-if="Field.Time" :id="Field.Name + 'date2'" v-model="Field.Value[1]" :value="Field.Value[1]" :Disabled="Field.Disabled" @update:model-value="OnInput(Field, $event)"/>
				</div>
				<label :for="Field.Name" class="flex items-center form-input-label">
					<div v-html="Field.Label"/>
				</label>
				<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
			</div>

			<div v-if="Field.Type === 'CopyLink'" :id="Field.Name + '-wrapper'" :key="Index" class="form-input" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div class="relative">
					<div class="input p-input text-info">
						<a :href="Field.Value" target="_blank" rel="noopener" :data-test="Field.DataTest">{{ Field.Value }}</a>
					</div>
					<button class="absolute right-0 ml-auto" style="top: 50%; margin-right: 8px; transform: translateY(-50%);" @click.prevent="CopyLink(Field.Value)">
						<OIcon class="text-neutral-600" name="copy"/>
					</button>
				</div>
				<label :for="Field.Name" class="flex items-center form-input-label" v-html="Field.Label"/>
			</div>

			<div v-if="Field.Type === 'Email-Input'" :id="Field.Name + '-wrapper'" :key="Index" class="form-input" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div v-if="Readonly" class="flex items-center">
					<i v-if="Field.Emblem" class="mr-2 form-input-emblem text-neutral-600" v-html="Field.Emblem"/>
					<span class="form-input-value" v-html="Field.Value ? Field.Value : '(brak)'"/>
				</div>
				<div v-else class="relative">
					<div class="flex items-center editInput">
						<input :id="Field.Name" v-model="Field.Value" style="border: none; color: black;" :type="GetFieldInputType(Field)" :disabled="Field.Disabled" :placeholder="Field.Placeholder" :maxlength="Field.Max" :data-test="`${Field.DataTest}__input`" @keyup="SetAsDirty" @mousewheel.prevent @change="OnInput(Field, $event)">
						<a class="px-2 geolocate-icon hover:text-accent" :data-test="`${Field.DataTest}__zmien`" @click="HandleClickEvent">Zmień</a>
					</div>
				</div>
				<label :for="Field.Name" class="flex items-center form-input-label">
					<div class="pr-2" v-html="Field.Label"/>
				</label>
				<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
			</div>

			<div v-if="Field.Type === 'Select'" :id="Field.Name + '-wrapper'" :key="Index" class="form-input" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div v-if="Readonly" class="form-input-value">
					<span class="form-input-value" v-html="Field.Value ? Field.Value : '(brak)'"/>
				</div>
				<div v-else class="form-input-group">
					<div class="relative">
						<DropdownSelect :id="Field.Name" v-model="Field.Value" :Items="Field.Items||[]" :disabled="Field.Disabled" :Placeholder="Field.Placeholder" class="w-full px-3 bg-white" @change="SetAsDirty(); OnInput(Field, $event)"/>
					</div>
				</div>
				<label :for="Field.Name" class="form-input-label" v-html="Field.Label"/>
				<div v-if="Field.Hint" class="form-input-validation form-input-hint | text-neutral-600 text-xs mt-2" v-html="Field.Hint"/>
				<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
			</div>

			<div v-if="Field.Type === 'Multiline'" :key="Index" class="form-input form-multiline" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div v-if="Readonly" class="form-input-value" v-html="Field.Value ? Field.Value : ''"/>
				<textarea v-else :id="Field.Name" v-model="Field.Value" :disabled="Field.Disabled" :placeholder="Field.Placeholder" :data-test="`${Field.DataTest}__textarea`" @keyup="SetAsDirty" @change="OnInput(Field, $event)"/>
				<label :for="Field.Name" class="flex items-center form-input-label">
					<slot :name="`${Field.Name}Label`"/>
					<div class="flex">
						<span v-if="Field.Required" class="mr-1 text-danger">*</span>
						<div v-html="Field.Label"/>
					</div>
					<hr class="flex-auto">
					<template v-if="!Readonly && Field.DisplayCount === 'colorless' && Field.Max && Field.Value.length">
						<span
							:data-test="`${Field.DataTest}__text_count`"
							:class="{
								'text-danger': Field.Value.length < Field.Min || Field.Value.length > Field.Max
							}" v-html="Field.Value.length + ' / ' + Field.Max"
						/>
					</template>
				</label>
				<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
			</div>

			<NipInput
				v-if="Field.Type === 'NipInput' && Field.UseComponent"
				ref="formGroupInput"
				:key="`Input${Index}`"
				v-model="Field.Value"
				:id="Field.Name + '-wrapper'"
				:class="Field.Class"
				:name="Field.Name"
				:label="Field.Label || ''"
				:dataTest="Field.DataTest"
				:forceDisabled="Field.Disabled"
				:doNotBlock="Field.DoNotBlock"
				:disableValidation="Field.DisableValidation"
				@dirty="SetAsDirty"
			/>
			<template v-if="Field.Type === 'NipInputAside' && Field.UseComponent">
				<div :class="Field.Class">
					<NipInput
					ref="formGroupInput"
					:key="`Input${Index}`"
					v-model="Field.Value"
					:class="Field.ComponentClass"
					:name="Field.Name"
					:dataTest="Field.DataTest"
					:force-disabled="Field.Disabled"
					:doNotBlock="Field.DoNotBlock"
					:disableValidation="Field.DisableValidation"
					@dirty="SetAsDirty"
				/>
				<slot :name="`${Field.Name}-nip-input-aside`"/>
				</div>
			</template>
			<div v-if="Field.Type === 'NipInput' && !Field.UseComponent" :key="Index" class="form-input" :class="Field.Class">
				<template v-if="!Readonly || Field.Value[1]">
					<div v-if="Readonly" class="form-input-value">
						<span v-if="Field.Value.country[0] !== 'PL'">{{ Field.Value.country.value }} </span>{{ Field.Value.value }}
					</div>
					<div v-else class="form-input-group form-input-nip">
						<div class="relative">
							<DropdownSelect v-model="Field.Value.country" :Items="countriesByCode" :Disabled="Field.Disabled" :data-test="`${Field.DataTest}__dropdown`" @change="SetAsDirty"/>
							<input :id="Field.Name" v-model="Field.Value.value" type="text" placeholder="0000000000" :data-test="`${Field.DataTest}__input`" :disabled="Field.Disabled" @keyup="SetAsDirty" @keydown="OnKeyDown(Field, $event)" @mousewheel.prevent @change="OnInput(Field, $event)">
						</div>
					</div>
					<label :for="Field.Name" class="form-input-label" v-html="Field.Label"/>
					<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
					<div v-if="Field.Disabled" class="[ form-input-validation ] text-neutral-600 text-xs mt-2">
						Zmiana NIP jest możliwa tylko przez kontakt z <a href="https://www.oferteo.pl/kontakt.html" target="_blank" class="page-link">Biurem obsługi Klienta</a> Oferteo.pl.
					</div>
				</template>
			</div>

			<template v-if="Field.Type === 'NipInputAside' && !Field.UseComponent">
				<div :key="Index" class="form-input" :class="Field.Class">
					<template v-if="!Readonly || Field.Value[1]">
						<div v-if="Readonly" class="form-input-value">
							<span v-if="Field.Value.country[0] !== 'PL'">{{ Field.Value.country.value }} </span>{{ Field.Value.value }}
						</div>
						<div v-else class="form-input-group form-input-nip">
							<div class="relative">
								<DropdownSelect v-model="Field.Value.country" :Items="countriesByCode" :Disabled="Field.Disabled" :data-test="`${Field.DataTest}__dropdown`" @change="SetAsDirty"/>
								<input :id="Field.Name" v-model="Field.Value.value" type="text" placeholder="0000000000" :data-test="`${Field.DataTest}__input`" :disabled="Field.Disabled" @keyup="SetAsDirty" @keydown="OnKeyDown(Field, $event)" @mousewheel.prevent @change="OnInput(Field, $event)">
							</div>
						</div>
						<label :for="Field.Name" class="form-input-label" v-html="Field.Label"/>
						<div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/>
						<div v-if="Field.Disabled" class="[ form-input-validation ] text-neutral-600 text-xs mt-2">
							Zmiana NIP jest możliwa tylko przez kontakt z <a href="https://www.oferteo.pl/kontakt.html" target="_blank" class="page-link">Biurem obsługi Klienta</a> Oferteo.pl.
						</div>
					</template>
				</div>
				<slot :name="`${Field.Name}-nip-input-aside`"/>
			</template>

			<div v-if="Field.Type === 'LocationInput'" :key="Index" class="form-input" :class="Field.Class" :data-invalid="Boolean(Field.ValidationMessage)">
				<div v-if="Readonly" class="form-input-value">
					{{ Field.Value.City.join(' ') }}
					<div v-if="Field.Value.Country[0] !== 'locationPl'">
						{{ Field.Value.Country[1] }}
					</div>
				</div>
				<template v-else>
					<LocationInput :id="Field.Name" :ref="Field.Name" v-model="Field.Value" :InFormGroup="true" :DataTest="Field.DataTest" @change="ValidateField(Field); SetAsDirty(Field, $event);"/>
					<label :for="Field.Name" class="form-input-label" v-html="Field.Label"/>
					<!-- <div class="[ form-input-validation ] text-danger text-xs mt-2" v-html="Field.ValidationMessage"/> -->
				</template>
			</div>

			<div v-if="Field.Type === 'PhotoGallery'" :key="Index" class="form-input" :class="Field.Class">
				<PhotoGallery v-model="Field.Value" :Readonly="Readonly" :Upload="Field.Upload" :Single="Field.Single" :Draggable="Field.Draggable" :AllowFiles="Field.AllowFiles" :mark-for-deletion="Field.MarkForDeletion" :AvatarMode="Field.AvatarMode" :MaxColumns="Field.MaxColumns" :UseCropWindow="Field.UseCrop" :onCustomerProfilePage="Field.onCustomerProfilePage" :DataTest="Field.DataTest" @update:model-value="GalleryChanged($event, 'input', Field)" @click="GalleryChanged($event, 'click', Field)" @upload="GalleryChanged($event, 'upload', Field)" :isVueRouterView="true"/>
			</div>

			<div v-if="Field.Type === 'PhotoGalleryWithAside'" :key="Index" :class="Field.Class">
				<div class="form-input" :class="Field.ComponentClass">
					<PhotoGallery v-model="Field.Value" :Readonly="Readonly" :Upload="Field.Upload" :Single="Field.Single" :Draggable="Field.Draggable" :AllowFiles="Field.AllowFiles" :mark-for-deletion="Field.MarkForDeletion" :AvatarMode="Field.AvatarMode" :MaxColumns="Field.MaxColumns" :UseCropWindow="Field.UseCrop" :onCustomerProfilePage="Field.onCustomerProfilePage" :DataTest="Field.DataTest" @update:model-value="GalleryChanged($event, 'input', Field)" @click="GalleryChanged($event, 'click', Field)" @upload="GalleryChanged($event, 'upload', Field)" :isVueRouterView="true"/>
				</div>
				<slot :name="`${Field.Name}-photo-gallery-aside`"/>
			</div>

			<div v-if="Field.Type === 'OptionList'" :key="Index" class="gap-2 form-input" :class="Field.Class" :data-test="Field.DataTest">
				<div class="order-first" v-text="Field.Label"/>
				<label v-for="(Option, i) in Field.Options" :key="i" :data-test="Option.dataTest" class="form-input-checkbox">
					<div class="input-control">
						<input v-model="Field.Value" type="radio" :value="Option.Value" @change="OnInput(Field, $event)"><div/>
					</div>
					{{ Option.Label }}
				</label>
			</div>

			<PhoneInput
				v-if="Field.Type === 'PhoneInput'"
				:ref="Field.Name"
				:key="`Input${Index}`"
				v-model="Field.Value"
				:class="Field.Class"
				:name="Field.Name"
				:dataTest="Field.DataTest"
				:Disabled="Field.Disabled"
				:Label="Field.Label"
				:Autocomplete="Field.HtmlAutocomplete"
				:HideValidation="Field.HideValidation"
				:Focus="Field.Focus"
				@update:model-value="OnPhoneInput(Field)"
				@invalid="PhoneInvalid"
			/>
		</template>
	</template>
	<slot/>
</form>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { mapState } from 'pinia';
import { FormGroupItem } from '@globals/types/components/FormGroup';
import { useLocationStore } from '@globals/stores/location';
import LocationInput from '../global/LocationInput.vue';
import NipInput from '../global/NipInput.vue';
import PhoneInput from '../global/PhoneInput.vue';
import PhotoGallery from '../global/PhotoGallery.vue';
import { copyToClipboard } from '@globals/utilities/Utils';

// @vuese
// Główny komponent odpowiedzialny za wyświetlanie formularzy, które użytkownik może edytować.
// @group Base Components | Forms
export default defineComponent({
	name: 'FormGroup',
	components: {
		LocationInput,
		NipInput,
		PhotoGallery,
		PhoneInput,
	},
	props: {
		Readonly: { type: Boolean, default: false },
		Form: { type: Array as PropType<FormGroupItem[]>, required: true },
		PreventAutocomplete: { type: Array, Boolean: false },
		noPadding: { type: Boolean, default: false },
	},
	emits: ['HandleClickEvent', 'dirty', 'gallery-changed', 'updated', 'phone-invalid'],
	data() {
		return {
			OriginalForm: '',
			Dirty: false,
		};
	},
	computed: {
		...mapState(useLocationStore, ['countries', 'countriesByCode']),
	},
	mounted() {
		if (!this.Readonly) {
			this.OriginalForm = JSON.stringify(this.Form);
		}
	},
	created() {},
	methods: {
		preventMousewheelOnNumericInput(e: WheelEvent) {
			const target = e.target as HTMLInputElement;
			if (target.type === 'number') {
				e.preventDefault();
			}
		},
		OnKeyDown(field, e) {
			if (e.ctrlKey) { return; }
			// Ignore E key for numeric fields
			if (field.Type === 'Input' && field.Validation && field.Validation.indexOf('Number') > -1) {
				if (e.keyCode === 69) {
					e.preventDefault();
				}
			}

			// Enable keyboard schortcuts for copy/paste
			const key = (e.key || '').toLowerCase();
			if (e.ctrlKey && ['c', 'v', 'x'].includes(key)) { return; }

			// Enable only numbers, + and space for phone number
			if (field.Validation && field.Validation.indexOf('Phone') > -1) {
				const printableCharacters = (e.keyCode >= 48 && e.keyCode <= 90) || (e.keyCode >= 106 && e.keyCode <= 111) || (e.keyCode >= 169 && e.keyCode <= 173) || (e.keyCode >= 169 && e.keyCode <= 173) || (e.keyCode >= 186 && e.keyCode <= 223);
				if ((printableCharacters && e.key !== '+' && e.key !== ' ' && !(e.keyCode >= 48 && e.keyCode <= 57)) || (e.shiftKey && e.key !== '+')) {
					e.preventDefault();
				}
				return;
			}

			// Special case for NIP input
			if (field.Type !== 'NipInput') { return; }

			// Ignore non-numeric characters for NIP fields
			if (((e.keyCode >= 40 && e.keyCode <= 126) && e.key !== '-' && !(e.keyCode >= 48 && e.keyCode <= 57)) || e.shiftKey) {
				e.preventDefault();
			}
		},
		CopyLink(url) {
			copyToClipboard(url);
		},
		HandleClickEvent() {
			this.$emit('HandleClickEvent');
		},
		GalleryChanged(item, eventType, field) {
			window.setTimeout(() => {
				this.Dirty = true; this.$emit('dirty');
			}, 1000);
			if (eventType !== 'input') {
				if (!field.UseCrop) {
					this.$emit('gallery-changed', { Item: item, Type: eventType, Field: field });
				}
			} else {
				field.Value = item;
			}
		},
		GetFieldByName(name: string) {
			return this.Form.find((f) => f.Name === name);
		},
		GetFieldInputType(f) {
			if (f.Currency) { return 'number'; }
			if (f.Validation) {
				if (f.Validation.indexOf('Phone') > -1) { return 'tel'; }
				if (f.Validation.indexOf('Email') > -1) { return 'email'; }
				if (f.Validation.indexOf('Number') > -1) { return 'number'; }
				if (f.Validation.indexOf('Currency') > -1) { return 'number'; }
				if (f.Validation.indexOf('Password') > -1) { return 'password'; }
				if (f.Validation.indexOf('PasswordIgnore') > -1) { return 'password'; }
			}
			if (f.Currency) { return 'number'; }
			return 'text';
		},
		AsObject(process = {}) : any {
			const o = {};
			this.Form.forEach((f) => {
				if (f.Name) {
					if (f.Name.indexOf('x-') === 0) {
						if (f.Name in process) {
							process[f.Name](o, f);
						}
					} else if (f.Name.indexOf('|') > -1) {
						const [fieldGroup, fieldName] = f.Name.split('|');
						if (!(fieldGroup in o)) { o[fieldGroup] = {}; }
						o[fieldGroup][fieldName] = f.Value;
					} else if (!Array.isArray(f.Name)) {
						o[f.Name] = f.Value;
					} else {
						for (const n in f.Name) { o[f.Name[n]] = f.Value[n]; }
					}
				}
			});
			return o;
		},

		ValidateField(field, isSilent = false) {
			if (!field.Validation) { return true; }

			const setValidation = (result: string) => {
				if (isSilent) { return; }
				field.ValidationMessage = result || '';
			};

			for (let v of field.Validation) {
				let result;
				let options = null;
				let customMessage = null;

				if (v.indexOf('|') > -1) {
					v = v.split('|');
					options = v[1].indexOf(',') ? v[1].split(',') : v[1];
					customMessage = v[2] || null;
					v = v[0];
				}

				if (field.Autocomplete && v === 'Required' && field.Value[0] < 1) {
					this.$set(field, 'ValidationMessage', 'Prosimy wybrać element z listy'); return false;
				}

				if (!(v in this.Validate)) {} else if (v.endsWith('Async')) { // eslint-disable-line no-empty
					// Przepisanie całej walidacji w sposób asynchroniczy byłoby odrobinę kosztowne,
					// dlatego też na chwilę obecną sprawdzamy jedynie przy zmianie pola. TODO!
					(async () => {
						result = await this.Validate[v](field.Value, options, this, field);
						if (result) { setValidation(result); }
					})();
				} else {
					result = this.Validate[v](field.Value, options, this, field);
					if (result && customMessage) { result = customMessage; }
					if (result) {
						setValidation(result);
						return false;
					}
				}

				if (field.Type === 'LocationInput') {
					try {
						result = this.$refs[field.Name][0].ValidateLocation();
						setValidation(result);
					} catch (err) {
						// ponieważ było readonly:C
						return true;
					}

					if (result) { return false; }
				}

				if (field.Type === 'PhoneInput') {
					try {
						result = this.$refs[field.Name][0].ValidatePhoneNumber();
						setValidation(result);
					} catch (err) {
						console.log(err);
						return true;
					}

					if (result) { return false; }
				}

				if (field.Autocomplete) {
					result = !field.Value[1];
					setValidation(result ? 'Prosimy wybrać pozycję z listy' : '');
					if (result) { return false; }
				}

				if (field.Type === 'Boolean' && v === 'Required' && !field.Value) {
					setValidation(customMessage || 'To pole jest wymagane.');
					return false;
				}
			}

			return true;
		},

		ValidateForm(isSilent = false) {
			let result = true;
			let invalidated = null;
			for (const field of this.Form) {
				if (!this.ValidateField(field, isSilent)) {
					result = false;
					if (!invalidated) { invalidated = field; }
				}
			}
			if (invalidated && !isSilent) {
				invalidated = document.querySelector(`#${invalidated.Name}-wrapper input`);
				if (invalidated) { invalidated.focus(); }
			}

			return result;
		},
		async ValidateFormComponents() {
			let result = true;
			const formGroupInputs = this.$refs.formGroupInput || [];
			for (const input of formGroupInputs) {
				if (input.validateField) {
					const validation = await input.validateField();
					if (!validation) { result = false; }
				}
			}
			return result;
		},
		SetAsDirty(e, f) {
			if (!this.Dirty && this.OriginalForm !== JSON.stringify(this.Form)) {
				this.Dirty = true; this.$emit('dirty');
			}
			if (f && f.CustomHandler) { f.CustomHandler(e, f); }
			if (f) { f.ValidationMessage = ''; }
		},

		setAsClean() {
			this.Dirty = false;
		},

		/** TERAZ UŻYWAJMY TEGO */
		async validate() {
			const validated = this.ValidateForm();
			if (validated) {
				const validatedComponents = await this.ValidateFormComponents();
				return validatedComponents;
			}
			return validated;
		},

		/** TERAZ UŻYWAJMY TEGO */
		toObject(process = {}) {
			return this.AsObject(process);
		},

		OnInput(field) {
			this.$emit('updated', field, this.Form);
			if (field.Validation && field.Validation.indexOf('Phone') > -1) {
				field.Value = (field.Value || '').replace(/[^+ \d]/g, '');
			}
			if (field.Type === 'NipInput') {
				field.Value.value = (field.Value.value || '').replace(/\D/g, '');
			}
			if (field.Type === 'PhoneInput') {
				field.Value = (field.Value || '').replace(/[^\+\d]/g, '');
			}
			const result = this.ValidateField(field);

			this.SetAsDirty();

			// Clear validation message.
			if (result && this.Dirty) {
				field.ValidationMessage = '';
			}

			return result;
		},
		OnPhoneInput(field) {
			this.$emit('updated', field, this.Form);
			field.Value = (field.Value || '').replace(/[^\+\d]/g, '');
		},
		PhoneInvalid(s) {
			this.$emit('phone-invalid', s);
		},
		TogglePassword(e) {
			const field = e.target.closest('.form-input').querySelector('input');
			const isPassword = field.type === 'password';
			e.target.classList.toggle('is-visible', isPassword);
			field.type = isPassword ? 'text' : 'password';
		},
		OnStaticClick(field, e) {
			if (field.CustomHandler) { field.CustomHandler(e, field); }
		},
	},
});
</script>

<style lang="scss">
input:-webkit-autofill {
	box-shadow: inset 0 0 0 200px white;
}
// disable eye from pasword field
.form-input-password-toggle i {
	background: #fff;
	border-radius: 100%;
	box-shadow: 0 0 0 8px #fff;
	pointer-events: none;
}

.form-input-password-toggle:not(.is-visible) .form-input-password-toggle-off,
.form-input-password-toggle.is-visible .form-input-password-toggle-on {
	display: none !important;
}

.form-heading {
	margin-top: 20px;
	margin-bottom: 20px;
	font-size: 16px;
	font-weight: 800;
}

.form-helper-text {
	margin-bottom: 16px;
	color: #6c6c6c;
}

.form-heading + .form-helper-text {
	margin-top: -20px;
}

.form-input {
	position: relative;
	display: flex;
	flex-direction: column;
	margin-bottom: 16px;

	label {
		order: -1;
		color: black;
	}
}

.is-readonly {
	.form-input {
		&.is-readonly-join { width: 100%; margin-bottom: -8px; }
		&:last-child { margin-bottom: 0; }
		label { font-weight: bold; }
	}
}

.is-editable {
	.form-input {
		label {
			margin-bottom: 4px;
		}
	}
}

input,
select,
textarea,
.like-input,
.input {
	width: 100%;
	min-height: 52px;
	padding: 16px 20px;
	border: 1px solid rgba(0, 0, 0, 0.15);
	border-radius: 4px;
	transition: border-color 0.15s ease-in;

	&:hover {
		border-color: rgba(0, 0, 0, 0.3);
	}

	&:focus {
		border-color: rgba(0, 0, 0, 0.6);
		outline: none;
	}
}

.p-input {
	padding: 16px 20px !important;
}

.input {
	padding: 0;

	input,
	select,
	textarea {
		border: none;
	}
}

.form-input[data-invalid]:not([data-invalid="false"]),
.form-input.is-not-valid {
	input,
	select,
	textarea,
	.input {
		border-color: var(--danger);

		&::placeholder {
			color: var(--danger);
			opacity: 0.75;
		}
	}

	label {
		color: var(--danger);
		transition: color 0.15s ease-in;
	}
}

.form-input[data-valid],
.form-input.is-valid {
	input,
	select,
	textarea,
	.input {
		border-color: var(--success);

		&::placeholder {
			color: var(--success);
			opacity: 0.75;
		}
	}
}

.is-readonly .form-hide-readonly { display: none; }
.is-editable .form-hide-editable { display: none; }

.form-input-group {
	> div:first-child {
		display: flex;
		align-items: center;
		margin-top: 2px;
		margin-bottom: 2px;
		border-radius: 4px;
		box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
		white-space: nowrap;

		> *:first-child {
			border-bottom-left-radius: 0.4rem !important;
			border-top-left-radius: 0.4rem !important;
		}

		> *:last-child {
			border-bottom-right-radius: 0.4rem;
			border-top-right-radius: 0.4rem;
		}

		input {
			min-height: 48px;
			padding-top: 16px;
			padding-bottom: 16px;
			margin-top: 0;
			margin-bottom: 0;
		}

		button {
			height: 48px;
			background: var(--white);
			border-radius: 0;
		}
	}
}

.form-multiline textarea {
	overflow: auto;
	min-height: 160px;
	resize: vertical;
}

.form-multiline textarea#description {
	min-height: 160px;
}

textarea {
	line-height: 1.5;
}

.autocomplete {
	position: relative;
	width: 100%;
	background: var(--white);
	// border: 1px solid gray;
	input { width: 100%; border: none !important; background: transparent; }

	&:focus-within {
		border-color: rgba(0, 0, 0, 0.6);
	}
}

.dropdown {
	position: relative;
}

.dropdown > .dropdown-button {
	display: flex;
	width: 100%;
	align-items: center;
	justify-content: space-between;
	padding-right: 4px;
}

.dropdown:not(.is-arrowless) > .dropdown-button::after {
	width: 12px;
	height: 12px;
	margin: 4px;
	background: currentColor;
	clip-path: polygon(50% 75%, 15% 30%, 85% 30%);
	content: '';
}

.dropdown > button[disabled] {
	width: auto;
	cursor: inherit;
	opacity: 0.5;

	&::after {
		content: '';
	}
}

.form-input > .dropdown > button {
	height: 50px;
	padding-left: 20px;
}

input:disabled {
	background: #fff;
	color: #6c6c6c;
}

.autocomplete .suggestions-list,
.dropdown .suggestions-list {
	position: absolute;
	z-index: 4;
	top: 64px;
	right: 0;
	left: 0;
	overflow: auto;
	min-width: 100%;
	border: 1px solid var(--neutral-300);
	background: var(--white);
	border-radius: 4px;
	transition: opacity, max-height;
}

.dropdown.is-arrowless .suggestions-list {
	top: calc(100% + 0.5rem);
}

::-webkit-scrollbar {
	width: 8px;
	height: 0;
}

::-webkit-scrollbar-button {
	width: 0;
	height: 0;
}

::-webkit-scrollbar-thumb {
	border: 0 none transparent;
	background: var(--accent);
	border-radius: 0;
}

::-webkit-scrollbar-track {
	border: 0 none transparent;
	background: transparent;
	border-radius: 16px;
}

::-webkit-scrollbar-corner {
	background: transparent;
}

.dropdown .suggestions-list {
	left: auto;
}

.autocomplete .suggestions-list:empty,
.autocomplete .suggestions-list,
.dropdown .suggestions-list:empty,
.dropdown .suggestions-list {
	z-index: 2048;
	max-height: 0;
	opacity: 0;
	transition-delay: 0.3s;
	transition-duration: 0.3s;
}

.dropdown:not(.is-focused) .suggestions-list {
	overflow: hidden !important;
	overflow: hidden;
	width: 0;
	min-width: 0;
	border: 1px solid transparent;
	background: transparent;
}

.autocomplete.is-focused input,
.dropdown.is-focused .dropdown-button,
.autocomplete input:focus,
.dropdown > .dropdown-button:focus {
	border-bottom-color: #ccc;
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
}

.form-input-group > .dropdown:focus-within {
	box-shadow: 0 0 0 1px #0009;
}

.dropdown.is-focused::before {
	position: fixed;
	z-index: 2047;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	content: '';
}

.autocomplete.is-focused input + .suggestions-list:not(:empty),
.dropdown.is-focused button + .suggestions-list:not(:empty),
.autocomplete input:focus + .suggestions-list:not(:empty),
.myoferteo .dropdown > button:focus + .suggestions-list:not(:empty) {
	max-height: 208px;
	border: 1px solid var(--neutral-300);
	background: white;
	opacity: 1;
	transition-delay: 0s;
	transition-duration: 0.3s;
}

.autocomplete .suggestions-list a,
.dropdown .suggestions-list a {
	display: block;
	padding: 8px 20px;
	color: var(--black);
	cursor: default;
}

.dropdown .suggestions-list a {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.autocomplete input:not(:focus) + .suggestions-list a {
	overflow: hidden !important;
	max-height: 0;
	padding: 0;
	transition-delay: 0.3s;
	transition-duration: 0.3s;
}
.autocomplete input:focus + .suggestions-list a { max-height: 64px; }

.autocomplete .suggestions-list a:hover,
.autocomplete .suggestions-list a.is-active,
.dropdown .suggestions-list a:hover,
.dropdown .suggestions-list a.is-active {
	background: #f5f5f5;
	text-decoration: none;
}

.form-input-emblem svg {
	position: absolute;
	top: 0;
	left: 0;
	width: 24px;
	height: 24px;
}

.form-group.is-editable.is-multicolumn:not(.is-dialog-form) {
	display: flex;
	max-width: 100%;
	flex-wrap: wrap;
	margin-right: -8px;
	margin-left: -8px;
}

.form-group.is-editable.is-multicolumn:not(.is-dialog-form) > * {
	padding-right: 8px;
	padding-left: 8px;
}

.form-group.is-editable.is-multicolumn:not(.is-dialog-form) > *:not([class*="col-"]) {
	flex: 1 1 100%;
}

.suggestions-list .suggestions-list-hide {
	display: none;
}

.dropdown-button .dropdown-button-hide {
	display: none;
}

.form-input-group .dropdown-button:disabled {
	background: #fff !important;
}

.form-input-nip input {
	padding-left: 0;
	border: none;
}

.form-input-nip button,
.form-input-phone button {
	width: 72px;
	height: 52px;
	justify-content: flex-start;
	padding-left: 20px;
}

.form-input-nip .suggestions-list,
.form-input-phone .suggestions-list {
	right: auto;
	left: 0;
}

.form-input-phone input {
	padding-left: 0;
}

.form-input-phone button {
	width: fit-content;
	justify-content: flex-start;
	padding-left: 1.25rem;
	padding-right: 0.5rem
}

.editInput {
	width: 100%;
	min-height: 52px;
	border: 1px solid rgba(0, 0, 0, 0.15);
	border-radius: 4px;
	transition: border-color 0.15s ease-in;
}

.form-input[data-invalid]:not([data-invalid="false"]) .input-control input[type=checkbox] + div {
	box-shadow: inset 0 0 0 2px var(--danger) !important;
}

.form-input-group > div:first-child {
	height: 53px;
}

.datepicker-full .datepicker {
	width: 100%;
}

.datepicker-full> .flex {
	display: block;
}

.form-group-section-separator {
	font-weight: bold;
	color: var(--neutral-700);
	display: flex;
	align-items: center;
	gap: 0.5rem;
	margin: 1rem 0 0.5rem;
}

.form-group-section-separator:after {
	content: '';
	height: 1px;
	flex-grow: 1;
	background-color: var(--neutral-100);
}
</style>
